import { link } from "@/components/Action.tsx";
import { type ApiClient, getResponseData } from "@/modules/api/apiClient.ts";
import { getResourceIconClass } from "@/modules/api/resourceTypes.tsx";
import { getQueryKey as getIpQueryKey } from "@/modules/network/ipAddress/ipAddressApi.ts";
import { getQueryKey as getVpcQueryKey } from "@/modules/network/vpc/vpcApi.ts";
import { dismissToast, raiseProgressToast, raiseRequestToast, type Toast } from "@/modules/notifications/toast.tsx";
import { serviceLink } from "@/modules/services/links.ts";
import type { ServiceCreateMutation } from "@/modules/services/service.store.ts";
import { jsonEncodedBody } from "@/utils/fetchClient.ts";
import { optimisticPromise } from "@/utils/query/optimisticPromise.ts";
import { produceQueryData } from "@/utils/query/produceQueryData.ts";
import { atomFromStandardQueryOptions } from "@/utils/query/queryAtom.ts";
import { mutationOptions, runMutation } from "@/utils/query/runMutation.ts";
import type { QueryClient } from "@tanstack/query-core";
import { type QueryKey, queryOptions } from "@tanstack/react-query";
import type {
    KubernetesHibernationBody,
    KubernetesPropertiesBody,
    ManagedService,
    ServiceRebuildBody,
    ServiceSecrets,
    ServiceUpdateBody,
    ServiceWhiteList,
    ServiceWhitelistEntryBody,
} from "@warrenio/api-spec/spec.oats.gen";
import { mapFromEntries } from "@warrenio/utils/collections/maps";
import { parallelMap } from "@warrenio/utils/collections/parallelMap";
import { assertNotNull } from "@warrenio/utils/notNull";
import { MINUTES } from "@warrenio/utils/timeUnits";

export interface PackageParams {
    uuid: string;
}

export type ManagedServiceLoc = ManagedService & {
    $type: "managed_service";
    location: string;
};

/** Map of {@link ManagedService}s keyed by ID */
export type Response = Map<ManagedService["uuid"], ManagedServiceLoc>;

const emptyResponse: Response = new Map();

const toastOptions: Partial<Toast> = { icon: getResourceIconClass("managed_service") };

function getQueryKey(): QueryKey {
    return ["/service/packages"];
}

function getSecretQueryKey(params?: PackageParams): QueryKey {
    return params == null ? ["/service/package/{uuid}/secrets"] : ["/service/package/{uuid}/secrets", params.uuid];
}

function getWhitelistQueryKey(params?: PackageParams): QueryKey {
    return params == null
        ? ["/service/package/{uuid}/whitelist_addresses"]
        : ["/service/package/{uuid}/whitelist_addresses", params.uuid];
}

function serviceFromData(data: ManagedService): ManagedServiceLoc {
    return { ...data, $type: "managed_service", location: data.properties.location };
}

function getQuery(client: ApiClient) {
    return queryOptions({
        queryKey: getQueryKey(),
        queryFn: async ({ signal }): Promise<Response> => {
            return mapFromEntries(
                getResponseData(
                    await client.GET("/user-resource/service/packages", {
                        signal,
                    }),
                ),
                (service) => [service.uuid, serviceFromData(service)] as const,
            );
        },
    });
}

export const allServicesQueryAtom = atomFromStandardQueryOptions(getQuery);

export function getServiceSecretsQuery(apiClient: ApiClient, params: PackageParams) {
    return queryOptions({
        queryKey: getSecretQueryKey(params),
        queryFn: async ({ signal }): Promise<ServiceSecrets> => {
            const { uuid } = params;
            return getResponseData(
                await apiClient.GET("/user-resource/service/package/{uuid}/secrets", {
                    signal,
                    params: { path: { uuid } },
                }),
            );
        },
    });
}

//#region Service Whitelist

export function getServiceWhitelistQuery(apiClient: ApiClient, params: PackageParams) {
    return queryOptions({
        queryKey: getWhitelistQueryKey(params),
        queryFn: async ({ signal }): Promise<ServiceWhiteList> => {
            const { uuid } = params;
            return getResponseData(
                await apiClient.GET("/user-resource/service/package/{uuid}/whitelist_addresses", {
                    signal,
                    params: { path: { uuid } },
                }),
            );
        },
    });
}

export function addServiceWhitelistAddressMutation(apiClient: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: getWhitelistQueryKey(),
        async mutationFn({ body, uuid }: { body: ServiceWhitelistEntryBody; uuid: string }) {
            return getResponseData(
                await apiClient.POST("/user-resource/service/package/{uuid}/whitelist_addresses", {
                    ...jsonEncodedBody,
                    params: { path: { uuid } },
                    body,
                }),
            );
        },
        async onSettled(_res, err, _mutation) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Allowlist address added",
                error: "Error adding allowlist address",
            });
            await queryClient.invalidateQueries({ queryKey: getWhitelistQueryKey() });
        },
    });
}

export function deleteServiceWhitelistAddressMutation(apiClient: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: getWhitelistQueryKey(),
        async mutationFn({ body, uuid }: { body: ServiceWhitelistEntryBody; uuid: string }) {
            return getResponseData(
                await apiClient.DELETE("/user-resource/service/package/{uuid}/whitelist_addresses", {
                    ...jsonEncodedBody,
                    params: { path: { uuid } },
                    body,
                }),
            );
        },
        async onSettled(_res, err, _mutation) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Allowlist address deleted",
                error: "Error deleting allowlist address",
            });
            await queryClient.invalidateQueries({ queryKey: getWhitelistQueryKey() });
        },
    });
}

//#endregion Service Whitelist

export function createServiceMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["managed_service/create"],
        async mutationFn({ body }: ServiceCreateMutation) {
            return serviceFromData(
                getResponseData(
                    await api.POST("/user-resource/service/package", {
                        ...jsonEncodedBody,
                        body,
                    }),
                ),
            );
        },
        onMutate(mutation) {
            raiseServiceProgressToast(mutation.requestUuid, `Creating Service [${mutation.body.display_name}]`);
            return { queryKey: getQueryKey() };
        },
        onSuccess(response, _mutation, { queryKey }) {
            produceQueryData(queryClient, queryKey, emptyResponse, (draft) => {
                draft.set(response.uuid, response);
            });
        },
        async onSettled(res, err, mutation, context) {
            dismissToast(mutation.requestUuid);
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Service created",
                successType: "success",
                viewLink: res ? serviceLink(res) : undefined,
                error: "Error creating Service",
                retryLink: link({
                    to: "/services/create",
                    state: { serviceCreateMutation: mutation },
                }),
            });

            assertNotNull(context);
            const { queryKey } = context;
            const {
                body: {
                    package_parameters: { location },
                },
            } = mutation;

            optimisticPromise(queryClient.invalidateQueries({ queryKey }));
            // XXX: Maybe should have same logic as `vmCreateMutation`?
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: getIpQueryKey({ location }) }),
                queryClient.invalidateQueries({ queryKey: getVpcQueryKey({ location }) }),
            ]);
        },
    });
}

export function deleteServiceMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["managed_service/delete"],
        async mutationFn({ uuid }: { uuid: string }) {
            return getResponseData(
                await api.DELETE("/user-resource/service/package/{uuid}", {
                    ...jsonEncodedBody,
                    params: { path: { uuid } },
                }),
            );
        },
        async onSettled(_res, err, _mutation) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Service deleted",
                error: "Error deleting Service",
            });
            await queryClient.invalidateQueries({ queryKey: getQueryKey() });
            await queryClient.invalidateQueries({ queryKey: getIpQueryKey() });
        },
    });
}

export function updateServiceMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["managed_service/update"],
        async mutationFn({ body, uuid }: { body: ServiceUpdateBody; uuid: string }) {
            return getResponseData(
                await api.PATCH("/user-resource/service/package/{uuid}", {
                    ...jsonEncodedBody,
                    body,
                    params: { path: { uuid } },
                }),
            );
        },
        async onSettled(_res, err, _mutation) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Service updated",
                error: "Error updating Service",
            });
            await queryClient.invalidateQueries({ queryKey: getQueryKey() });
        },
    });
}

function startServiceResourceMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["managed_service/start"],
        async mutationFn({ uuid, target_uuid }: { uuid: string; target_uuid: string }) {
            return getResponseData(
                await api.POST("/user-resource/service/package/{uuid}/resources/{target_uuid}/start", {
                    params: { path: { uuid, target_uuid } },
                }),
            );
        },
        async onSettled(_res, err, _mutation) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Starting Service VM done",
                error: "Error starting Service VM",
            });
            await queryClient.invalidateQueries({ queryKey: getQueryKey() });
        },
    });
}

function stopServiceResourceMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["managed_service/stop"],
        async mutationFn({ uuid, target_uuid }: { uuid: string; target_uuid: string }) {
            return getResponseData(
                await api.POST("/user-resource/service/package/{uuid}/resources/{target_uuid}/stop", {
                    params: { path: { uuid, target_uuid } },
                }),
            );
        },
        async onSettled(_res, err, _mutation) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Stopping Service VM done",
                error: "Error stopping Service VM",
            });
            await queryClient.invalidateQueries({ queryKey: getQueryKey() });
        },
    });
}

export function startServiceMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        async mutationFn({ body }: { body: ManagedServiceLoc }) {
            const { uuid, resources } = body;
            const vms = resources.filter((r) => r.resource_type === "vm");
            return await parallelMap(vms, async (vm) => {
                return await runMutation(queryClient, startServiceResourceMutation(api, queryClient), {
                    uuid,
                    target_uuid: vm.resource_id,
                });
            });
        },
    });
}

export function stopServiceMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        async mutationFn({ body }: { body: ManagedServiceLoc }) {
            const { uuid, resources } = body;
            const vms = resources.filter((r) => r.resource_type === "vm");
            return await parallelMap(vms, async (vm) => {
                return await runMutation(queryClient, stopServiceResourceMutation(api, queryClient), {
                    uuid,
                    target_uuid: vm.resource_id,
                });
            });
        },
    });
}

export function hibernateServiceResourceMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["managed_service/hibernate"],
        async mutationFn({ uuid, body }: { uuid: string; body: KubernetesHibernationBody }) {
            return getResponseData(
                await api.POST("/user-resource/service/package/{uuid}/action/hibernate", {
                    ...jsonEncodedBody,
                    body,
                    params: { path: { uuid } },
                }),
            );
        },
        async onSettled(_res, err, _mutation) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Toggling Service hibernation done",
                error: "Error toggling Service hibernation",
            });
            await queryClient.invalidateQueries({ queryKey: getQueryKey() });
        },
    });
}

export function rebuildServiceMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        async mutationFn({ body, location }: { location: string; body: ServiceRebuildBody }) {
            return getResponseData(
                await api.POST("/{location}/user-resource/service/package/vm/rebuild", {
                    ...jsonEncodedBody,
                    body,
                    params: { path: { location } },
                }),
            );
        },
        async onSettled(_res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Restoring Service backup done",
                error: "Error restoring Service backup",
            });
            await queryClient.invalidateQueries({ queryKey: getQueryKey() });
        },
    });
}

export function updateServicePropertiesMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["managed_service/properties"],
        async mutationFn({ body, uuid }: { body: KubernetesPropertiesBody; uuid: string }) {
            return getResponseData(
                await api.PATCH("/user-resource/service/package/{uuid}/properties", {
                    ...jsonEncodedBody,
                    body,
                    params: { path: { uuid } },
                }),
            );
        },
        async onSettled(_res, err, _mutation) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Service updated",
                error: "Error updating Service",
            });
            await queryClient.invalidateQueries({ queryKey: getQueryKey() });
        },
    });
}

//#region toasts

/** PostgreSQL might take a long time */
export const SERVICE_CREATE_TIMER = 5 * MINUTES;

function raiseServiceProgressToast(toastId: string, message: string) {
    raiseProgressToast({
        ...toastOptions,
        label: message,
        lifeTime: SERVICE_CREATE_TIMER,
        toastId,
    });
}

//endregion toasts
